import React, { useState, useEffect } from 'react';
import { Typography, Box, IconButton, Tooltip as MuiTooltip, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { TabIndex } from '../navigation';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useWithTokenRefresh } from '../utils/api';
import { buildTimeSeriesParams, buildBreakdownParams, formatV2Series } from '../utils/v2StatsHelpers';

// Import logos for AI providers
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';

// Pie chart colors for LLMs
const LLM_COLORS = ['#617BFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

interface AIOverviewProps {
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

interface V2ModelEntity {
  entityId: string;
  entityName: string;
  totals: { requests: number; tokensIn: number; tokensOut: number };
}

interface V2SeriesPoint {
  ts: number;
  tokensIn: number;
  tokensOut: number;
  requests: number;
}

const AIOverview: React.FC<AIOverviewProps> = ({ handleTabChange }) => {
  const navigate = useNavigate();

  // Helper function to navigate to a tab using custom events
  const navigateToTab = (tabIndex: number, path: string) => {
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    }
    window.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    navigate(path);
  };

  const { apiClient } = useApi();
  const { authState, globalData } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();
  const selectedWorkspaceId = useSelectedWorkspace();

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [modelEntities, setModelEntities] = useState<V2ModelEntity[]>([]);
  const [workspaceAiLlmIds, setWorkspaceAiLlmIds] = useState<string[]>([]);
  const [llmNamesMap, setLlmNamesMap] = useState<Record<string, string>>({});
  const [llmTypesMap, setLlmTypesMap] = useState<Record<string, string>>({});

  // State for token visibility toggle in chart
  const [visibleTokens, setVisibleTokens] = useState({ tokensIn: true, tokensOut: true });

  const [v2Series, setV2Series] = useState<V2SeriesPoint[]>([]);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);

  const tokenActivityData = React.useMemo(() => {
    if (v2Series.length === 0) {
      const now = new Date();
      const emptyDays = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        emptyDays.push({ date: `${month}/${day}`, tokensIn: 0, tokensOut: 0 });
      }
      return emptyDays;
    }
    return formatV2Series(v2Series, '30d').map(p => ({
      date: p.name,
      tokensIn: p.tokensIn,
      tokensOut: p.tokensOut,
    }));
  }, [v2Series]);

  // Calculate totals from token activity data
  const totalTokensInFromChart = React.useMemo(() => 
    tokenActivityData.reduce((sum, item) => sum + item.tokensIn, 0), [tokenActivityData]);
  const totalTokensOutFromChart = React.useMemo(() => 
    tokenActivityData.reduce((sum, item) => sum + item.tokensOut, 0), [tokenActivityData]);

  // Get user first name
  const firstName = globalData?.userProfile?.name || authState?.user?.name || 'User';

  const aggregatedStats = React.useMemo(() => {
    return v2Series.reduce((acc, p) => ({
      totalApiCalls: acc.totalApiCalls + (p.requests || 0),
      totalTokensIn: acc.totalTokensIn + (p.tokensIn || 0),
      totalTokensOut: acc.totalTokensOut + (p.tokensOut || 0),
    }), {
      totalApiCalls: 0,
      totalTokensIn: 0,
      totalTokensOut: 0,
    });
  }, [v2Series]);

  const top5LlmData = React.useMemo(() => {
    if (modelEntities.length === 0) return [];
    const sorted = modelEntities
      .map((entity) => ({
        id: entity.entityId,
        name: entity.entityName || entity.entityId,
        type: '',
        tokensIn: entity.totals.tokensIn,
        tokensOut: entity.totals.tokensOut,
        totalTokens: entity.totals.tokensIn + entity.totals.tokensOut,
        calls: entity.totals.requests,
        color: '',
      }))
      .sort((a, b) => b.totalTokens - a.totalTokens);

    const maxToShow = Math.min(sorted.length, 5);
    return sorted.slice(0, maxToShow).map((llm, index) => ({
      ...llm,
      color: LLM_COLORS[index % LLM_COLORS.length],
    }));
  }, [modelEntities]);

  // Total tokens for Top 5
  const top5TotalTokens = React.useMemo(() => 
    top5LlmData.reduce((sum, llm) => sum + llm.totalTokens, 0), [top5LlmData]);

  // Pie chart data for Top 5 LLMs
  const llmPieChartData = React.useMemo(() => {
    if (top5TotalTokens === 0) {
      return [{ name: 'No data', value: 1, color: '#E5E7EB' }];
    }
    return top5LlmData.filter(llm => llm.totalTokens > 0).map(llm => ({
      name: llm.name,
      value: llm.totalTokens,
      color: llm.color,
    }));
  }, [top5LlmData, top5TotalTokens]);


  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Get provider icon with color indicator dot
  const getProviderIconWithColor = (aiType: string, color: string): JSX.Element => {
    const iconContent = (() => {
      switch (aiType?.toUpperCase()) {
        case 'OPENAI':
          return (
            <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }} fill="#10A37F">
              <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4046-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
            </svg>
          );
        case 'ANTHROPIC':
          return <img src={anthropicLogo} alt="Anthropic" style={{ width: '20px', height: '20px' }} />;
        case 'GEMINI':
        case 'GOOGLE':
          return <img src={googleCloudLogo} alt="Google" style={{ width: '20px', height: '20px' }} />;
        case 'AWS':
        case 'BEDROCK':
          return <img src={awsLogo} alt="AWS" style={{ width: '20px', height: '20px' }} />;
        default:
          return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          );
      }
    })();

    return (
      <div style={{ 
        width: 40, 
        height: 40, 
        borderRadius: 8, 
        backgroundColor: '#F9FAFB', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #E5E7EB'
      }}>
        {iconContent}
      </div>
    );
  };

  // Keep original getProviderIcon for backward compatibility
  const getProviderIcon = (aiType: string): JSX.Element => {
    switch (aiType?.toUpperCase()) {
      case 'OPENAI':
        return (
          <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px' }} fill="#10A37F">
              <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4046-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
            </svg>
          </div>
        );
      case 'ANTHROPIC':
        return (
          <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={anthropicLogo} alt="Anthropic" style={{ width: '18px', height: '18px' }} />
          </div>
        );
      case 'GEMINI':
      case 'GOOGLE':
        return (
          <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={googleCloudLogo} alt="Google" style={{ width: '18px', height: '18px' }} />
          </div>
        );
      case 'AWS':
      case 'BEDROCK':
        return (
          <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={awsLogo} alt="AWS" style={{ width: '18px', height: '18px' }} />
          </div>
        );
      default:
        return (
          <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
        );
    }
  };

  useEffect(() => {
    setModelEntities([]);
    setV2Series([]);
    setWorkspaceAiLlmIds([]);
    setLlmNamesMap({});
  }, [selectedWorkspaceId]);

  const loadAllAiStats = async () => {
    if (!authState?.token) return;
    try {
      setIsLoading(true);
      setDailyStatsLoading(true);

      const workspaceId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
      const llmsResponse = await withTokenRefresh(() => apiClient.getAiLlms(workspaceId));

      const llmIds: string[] = [];
      const namesMap: Record<string, string> = {};
      const typesMap: Record<string, string> = {};
      if (llmsResponse.success && llmsResponse.aiLlms) {
        llmsResponse.aiLlms.forEach((llm: any) => {
          if (llm.id) {
            llmIds.push(llm.id);
            namesMap[llm.id] = llm.name || '';
            typesMap[llm.id] = llm.aiType || llm.llmType || '';
          }
        });
      }
      setWorkspaceAiLlmIds(llmIds);
      setLlmNamesMap(namesMap);
      setLlmTypesMap(typesMap);

      const wsId = workspaceId || null;
      const tsParams = buildTimeSeriesParams(wsId, null, '30d');
      const bdParams = buildBreakdownParams(wsId, null, '30d');

      const [timeSeriesRes, modelsRes] = await Promise.all([
        withTokenRefresh(() => apiClient.getAiGatewayStats(tsParams as any)),
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('models', bdParams as any)),
      ]);

      if ((timeSeriesRes as any)?.success && (timeSeriesRes as any)?.series) {
        setV2Series((timeSeriesRes as any).series);
      }

      if ((modelsRes as any)?.success && (modelsRes as any)?.entities) {
        setModelEntities((modelsRes as any).entities.map((e: any) => ({
          entityId: e.entityId,
          entityName: e.entityName || e.entityId,
          totals: {
            requests: e.totals?.requests || 0,
            tokensIn: e.totals?.tokensIn || 0,
            tokensOut: e.totals?.tokensOut || 0,
          },
        })));
      }
    } catch (error) {
      console.error('[AIOverview] Error loading AI stats:', error);
    } finally {
      setIsLoading(false);
      setDailyStatsLoading(false);
      setInitialLoadComplete(true);
    }
  };

  useEffect(() => {
    loadAllAiStats();
  }, [authState?.token, selectedWorkspaceId]);

  // Global loader during initial data fetch
  if (isLoading && !initialLoadComplete) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading AI statistics...</p>
        <p className="text-gray-500 text-sm mt-2">Fetching data and metrics</p>
      </div>
    );
  }

  const totalTokens = aggregatedStats.totalTokensIn + aggregatedStats.totalTokensOut;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          AI
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Configure and manage your AI integrations and language models
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Overview Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Overview
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Welcome back, {firstName}. You have made {formatNumber(aggregatedStats.totalApiCalls)} requests with {formatNumber(totalTokens)} tokens used.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <MuiTooltip title="Reload">
              <IconButton
                size="small"
                onClick={loadAllAiStats}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </MuiTooltip>
            {/* Add LLM */}
            <MuiTooltip title="Add">
              <IconButton
                size="small"
                onClick={() => { localStorage.setItem('triggerAddAILLM', '1'); navigateToTab(TabIndex.AILLM, '/ai/llm'); }}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>
      </Box>

      <div className="max-w-7xl mx-auto">
        {/* First Row - 4 Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* AI LLMs Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>AI LLMs</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{workspaceAiLlmIds.length}</Typography>
          </div>

          {/* Token In (30D) Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Token In (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatNumber(aggregatedStats.totalTokensIn)}</Typography>
          </div>

          {/* Token Out (30D) Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Token Out (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatNumber(aggregatedStats.totalTokensOut)}</Typography>
          </div>

          {/* Requests (30D) Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Requests (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatNumber(aggregatedStats.totalApiCalls)}</Typography>
          </div>
        </div>

        {/* AI LLM Activity Card - Full Width (same as Storage Activity) */}
        <div className="flex flex-col gap-6 mb-8">
          <div className="relative" style={{ width: '100%' }}>
            {/* Gray background card */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{ height: '518px', position: 'absolute', zIndex: 1 }}
            ></div>

            {/* Header */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px', margin: 0 }}
              >
                AI LLM Activity
              </Typography>
              <Typography
                style={{ color: '#535862', fontWeight: 500, fontSize: '13px', lineHeight: '20px' }}
              >
                Tokens: {formatNumber(top5TotalTokens)}
              </Typography>
            </div>

            {/* White card - Split layout: Pie chart left, LLM list right */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex rounded-xl"
              style={{ height: '476px', marginTop: '2px', position: 'relative', zIndex: 2 }}
            >
              {/* Left side - Pie Chart */}
              <div
                style={{
                  width: '420px',
                  minWidth: '420px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid #e5e7eb',
                  padding: '16px',
                }}
              >
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3" style={{ borderColor: '#617BFF' }}></div>
                    <p className="text-gray-600 text-sm font-medium">Loading data...</p>
                  </div>
                ) : top5TotalTokens === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #D5D7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, background: '#FFFFFF' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <div style={{ color: '#181D27', fontWeight: 600, fontSize: '16px', textAlign: 'center', marginBottom: 4 }}>
                      No token data
                    </div>
                    <div style={{ color: '#535862', fontWeight: 400, fontSize: '14px', textAlign: 'center' }}>
                      Add AI LLMs to see usage
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ width: '240px', height: '240px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={llmPieChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            outerRadius={110}
                            paddingAngle={0}
                            strokeWidth={0}
                            dataKey="value"
                            nameKey="name"
                            label={false}
                          >
                            {llmPieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number) => formatNumber(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend below - Same style as Storage */}
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      {llmPieChartData.map((entry, index) => (
                        <div key={index} className="flex items-center">
                          <div
                            className="w-2.5 h-2.5 mr-1"
                            style={{ backgroundColor: entry.color, display: 'inline-block' }}
                          ></div>
                          <Typography variant="caption" sx={{ fontSize: '11px' }}>{entry.name}</Typography>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Right side - LLM List */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header Row - Same style as Storage */}
                <div
                  style={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    borderTopRightRadius: '12px',
                  }}
                >
                  <div style={{ flex: 1, paddingLeft: '16px' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Name
                    </Typography>
                  </div>
                  <div style={{ width: '80px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Token In
                    </Typography>
                  </div>
                  <div style={{ width: '80px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Token Out
                    </Typography>
                  </div>
                  <div style={{ width: '80px', textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      style={{
                        color: '#6B7280',
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '18px',
                        textTransform: 'none',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Requests
                    </Typography>
                  </div>
                  <div style={{ width: '40px' }}></div>
                </div>

                {/* List of Top 5 LLMs */}
                <div style={{ flex: 1 }}>
                  {isLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3" style={{ borderColor: '#617BFF' }}></div>
                      <p className="text-gray-600 text-sm font-medium">Loading LLM data...</p>
                    </div>
                  ) : workspaceAiLlmIds.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #D5D7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, background: '#FFFFFF' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <div style={{ color: '#181D27', fontWeight: 600, fontSize: '16px', textAlign: 'center', marginBottom: 4 }}>
                        No AI LLM configurations
                      </div>
                      <div style={{ color: '#535862', fontWeight: 400, fontSize: '14px', textAlign: 'center', marginBottom: 16 }}>
                        Add your first LLM to see activity.
                      </div>
                      <Button
                        variant="contained"
                        onClick={() => { localStorage.setItem('triggerAddAILLM', '1'); navigateToTab(TabIndex.AILLM, '/ai/llm'); }}
                        style={{
                          borderRadius: 8,
                          background: '#617BFF',
                          color: '#FFFFFF',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '14px',
                        }}
                      >
                        <span style={{ fontSize: 16, marginRight: 6 }}>+</span> Add
                      </Button>
                    </div>
                  ) : (
                    top5LlmData.map((llm, index) => (
                      <div
                        key={llm.id || index}
                        style={{
                          height: '72px',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#FFFFFF',
                          borderBottom: index < 4 ? '1px solid #F0F0F0' : 'none',
                          cursor: llm.id.startsWith('empty-') ? 'default' : 'pointer',
                        }}
                        onClick={() => !llm.id.startsWith('empty-') && navigate(`/ai/llm?highlight=${llm.id}`)}
                      >
                        {/* Name + Logo Section */}
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            overflow: 'hidden',
                          }}
                        >
                          <div style={{ marginRight: '12px', flexShrink: 0 }}>
                            {getProviderIconWithColor(llm.type, llm.color)}
                          </div>
                          <Typography
                            variant="body2"
                            style={{
                              color: llm.id.startsWith('empty-') ? '#9CA3AF' : '#181D27',
                              fontWeight: 500,
                              fontSize: '14px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {llm.name}
                          </Typography>
                        </div>

                        {/* Tokens In Section */}
                        <div style={{ width: '80px', textAlign: 'center' }}>
                          <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                            {formatNumber(llm.tokensIn)}
                          </Typography>
                        </div>

                        {/* Tokens Out Section */}
                        <div style={{ width: '80px', textAlign: 'center' }}>
                          <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                            {formatNumber(llm.tokensOut)}
                          </Typography>
                        </div>

                        {/* Requests Section */}
                        <div style={{ width: '80px', textAlign: 'center' }}>
                          <Typography variant="body2" style={{ color: '#667085', fontSize: '13px', fontWeight: 400 }}>
                            {formatNumber(llm.calls)}
                          </Typography>
                        </div>

                        {/* Chevron arrow */}
                        <div style={{ width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {!llm.id.startsWith('empty-') && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Bottom Action Bar */}
                <div
                  style={{
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    padding: '0 16px',
                    borderTop: '1px solid #E5E7EB',
                    backgroundColor: 'white',
                    borderBottomRightRadius: '12px',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: '#617BFF',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '8px 16px',
                    }}
                    onClick={() => { localStorage.setItem('triggerAddAILLM', '1'); navigateToTab(TabIndex.AILLM, '/ai/llm'); }}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    style={{
                      height: '36px',
                      borderRadius: '8px',
                      border: '1px solid #D0D5DD',
                      backgroundColor: 'white',
                      color: '#344054',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '8px 16px',
                    }}
                    onClick={() => navigateToTab(TabIndex.AILLM, '/ai/llm')}
                  >
                    View all
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Row - Token Activity + Explore Our Guides */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Token Activity Card - Same style as Cloud Storage "Transfer Activity" */}
          <div className="relative" style={{ width: '528px', maxWidth: '100%' }}>
            {/* Carte grise de fond avec rayon de bordure */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{
                height: '317px',
                position: 'absolute',
                zIndex: 1,
              }}
            ></div>

            {/* Header avec fond légèrement gris */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{
                  color: '#181D27',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Token Traffic Rate
              </Typography>
              {/* Total with In/Out labels */}
              <Typography style={{ fontSize: '13px', color: '#535862', fontWeight: 500 }}>
                {formatNumber(totalTokensInFromChart + totalTokensOutFromChart)}{' '}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '4px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#617BFF', display: 'inline-block' }}></span>
                  <span>in</span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block' }}></span>
                  <span>out</span>
                </span>
              </Typography>
            </div>

            {/* Carte blanche par-dessus */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div style={{ height: 210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={tokenActivityData}>
                    <defs>
                      <linearGradient id="colorTokensIn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#617BFF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorTokensOut" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      stroke="#E5E7EB"
                      tickLine={false}
                      tickFormatter={(value, index) => {
                        // Show 6 labels: J-30, J-25, J-20, J-15, J-10, J-5, J-0
                        if (index === 0 || index === 5 || index === 10 || index === 15 || index === 20 || index === 25 || index === 29) {
                          return value;
                        }
                        return '';
                      }}
                      interval={0}
                    />
                    <YAxis
                      width={60}
                      tick={{ fontSize: 11, fill: '#6B7280', style: { whiteSpace: 'nowrap' } }}
                      stroke="#E5E7EB"
                      tickLine={false}
                      tickFormatter={(value) => formatNumber(value)}
                      domain={[0, (() => {
                        const maxVal = Math.max(
                          ...tokenActivityData.map(d => d.tokensIn),
                          ...tokenActivityData.map(d => d.tokensOut),
                          0
                        );
                        if (maxVal === 0) return 1000; // Fictive scale if no data
                        // Round to next "nice" number
                        const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
                        const normalized = maxVal / magnitude;
                        const rounded = Math.ceil(normalized);
                        return rounded * magnitude;
                      })()]}
                      tickCount={6}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', borderColor: '#ddd', borderRadius: '8px' }}
                      formatter={(value: number, name: string) => [
                        formatNumber(value),
                        name === 'tokensIn' ? 'Tokens In' : 'Tokens Out',
                      ]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    {visibleTokens.tokensIn && (
                      <Area
                        type="monotone"
                        dataKey="tokensIn"
                        stroke="#617BFF"
                        fill="url(#colorTokensIn)"
                        strokeWidth={2}
                        name="tokensIn"
                      />
                    )}
                    {visibleTokens.tokensOut && (
                      <Area
                        type="monotone"
                        dataKey="tokensOut"
                        stroke="#10B981"
                        fill="url(#colorTokensOut)"
                        strokeWidth={2}
                        name="tokensOut"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-2">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => setVisibleTokens((prev) => ({ ...prev, tokensIn: !prev.tokensIn }))}
                  style={{ opacity: visibleTokens.tokensIn ? 1 : 0.5 }}
                >
                  <div className="w-3 h-3 mr-1" style={{ backgroundColor: '#617BFF' }}></div>
                  <Typography variant="caption" sx={{ fontSize: '12px', color: '#6B7280' }}>Tokens In</Typography>
                </div>
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => setVisibleTokens((prev) => ({ ...prev, tokensOut: !prev.tokensOut }))}
                  style={{ opacity: visibleTokens.tokensOut ? 1 : 0.5 }}
                >
                  <div className="w-3 h-3 mr-1" style={{ backgroundColor: '#10B981' }}></div>
                  <Typography variant="caption" sx={{ fontSize: '12px', color: '#6B7280' }}>Tokens Out</Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Explore Our Guides Card */}
          <div className="relative" style={{ width: '602px', maxWidth: '100%' }}>
            {/* Carte grise de fond */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{ height: '317px', position: 'absolute', zIndex: 1 }}
            ></div>

            {/* Header */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}
              >
                Explore Our Guides
              </Typography>
            </div>

            {/* Carte blanche */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  maxHeight: 240,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="flex space-x-4" style={{ paddingBottom: '4px' }}>
                  {/* Guide 1: Configure an AI LLM */}
                  <div
                    style={{
                      width: 216,
                      height: 235,
                      minWidth: '216px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '20px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '22px', marginBottom: '8px' }}>
                        Configure an AI LLM
                      </Typography>
                      <Typography style={{ fontSize: '13px', color: '#6B7280', lineHeight: '18px' }}>
                        Set up your first AI language model integration
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage/create-a-bucket-1', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View guide <span style={{ fontSize: '16px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 2: AI LLM API Reference */}
                  <div
                    style={{
                      width: 216,
                      height: 235,
                      minWidth: '216px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '20px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '22px', marginBottom: '8px' }}>
                        AI LLM API Reference
                      </Typography>
                      <Typography style={{ fontSize: '13px', color: '#6B7280', lineHeight: '18px' }}>
                        Explore the complete AI LLM API documentation
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/support-reference/platform-api-reference/ai-apis', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View docs <span style={{ fontSize: '16px' }}>→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AIOverview;
